/**
 * Builds a Skia Path containing all soccer field lines for native rendering.
 * Mirrors the web drawSoccerField logic from useSquadPlanningField.
 * @module features/squad-planning/utils/buildFieldPath
 */

import { Skia } from "@shopify/react-native-skia";

/**
 * Builds a Skia Path with soccer field lines (boundary, center, penalty boxes, goals).
 * @param pad - Left padding (paddingContent from the field hook)
 * @param padH - Vertical padding (PADDING_HORIZONTAL)
 * @param fw - Field width
 * @param hc - Total height content
 * @returns A Skia Path ready for stroke rendering
 */
export function buildFieldPath(
  pad: number,
  padH: number,
  fw: number,
  hc: number
): ReturnType<typeof Skia.Path.Make> {
  const path = Skia.Path.Make();
  const fh = hc - padH * 2;

  // Guard: skip drawing if dimensions are invalid (pre-layout)
  if (fw <= 0 || fh <= 0) return path;

  const cx = fw / 2 + pad;
  const cy = fh / 2 + padH;
  const penaltyW = fw * 0.6;
  const penaltyH = fh * 0.16;
  const goalAreaW = fw * 0.3;
  const goalAreaH = fh * 0.06;
  const goalW = fw * 0.15;
  const goalH = fh * 0.03;

  // Outer boundary
  path.addRect(Skia.XYWHRect(pad, padH, fw, fh));
  // Center line
  path.moveTo(pad, cy);
  path.lineTo(pad + fw, cy);
  // Center circle
  path.addCircle(cx, cy, fw * 0.135);
  // Top penalty box + goal area
  path.addRect(Skia.XYWHRect(cx - penaltyW / 2, padH, penaltyW, penaltyH));
  path.addRect(Skia.XYWHRect(cx - goalAreaW / 2, padH, goalAreaW, goalAreaH));
  // Bottom penalty box + goal area
  path.addRect(
    Skia.XYWHRect(cx - penaltyW / 2, padH + fh - penaltyH, penaltyW, penaltyH)
  );
  path.addRect(
    Skia.XYWHRect(
      cx - goalAreaW / 2,
      padH + fh - goalAreaH,
      goalAreaW,
      goalAreaH
    )
  );
  // Goals
  path.addRect(Skia.XYWHRect(cx - goalW / 2, padH - goalH, goalW, goalH));
  path.addRect(Skia.XYWHRect(cx - goalW / 2, padH + fh, goalW, goalH));

  return path;
}
